/**
 * Product Builder — Custom Field Toolkit (Admin-Side / Inline)
 * Code is pasted into the settings metafield when type=Custom.
 * Executes inline via new Function(), no asset loading.
 */
(function () {
  'use strict';

  var PB = window.productBuilder;
  if (!PB) return;

  var instances = {};

  function uid() {
    return 'pbtk_' + Math.random().toString(36).slice(2, 9);
  }

  function escapeHTML(s) {
    return PB.escapeAttr ? PB.escapeAttr(s) : String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ── Sub-field Factories ─────────────────────────────── */

  function createTextField(instance, opts) {
    opts = opts || {};
    var label = opts.label || uid();
    var settings = {
      required: !!opts.required,
      min: parseInt(opts.min || 0, 10),
      max: parseInt(opts.max || 100, 10),
      uppercase: !!opts.uppercase,
      placeholder: opts.placeholder || 'Type here...'
    };
    var settingsStr = 'required:' + settings.required + ', min:' + settings.min + ', max:' + settings.max;
    if (settings.uppercase) settingsStr += ', uppercase:true';

    var wrap = document.createElement('div');
    wrap.className = 'pb-tk-field pb-tk-text-field';
    if (opts.fieldLabel) {
      wrap.innerHTML = '<span class="option-label">' + escapeHTML(opts.fieldLabel) + '</span>';
    }
    var input = document.createElement('input');
    input.type = 'text';
    input.className = 'builder-input pb-tk-input';
    input.placeholder = settings.placeholder;
    input.dataset.label = label;
    input.dataset.settings = settingsStr;
    wrap.appendChild(input);

    var callbacks = [];
    input.addEventListener('input', function () {
      if (settings.uppercase) input.value = input.value.toUpperCase();
      var val = input.value.trim();
      instance._values[label] = val;
      instance._syncToState();
      if (PB.validateTextInput) PB.validateTextInput(input, settings);
      if (PB.updateInputMeta) PB.updateInputMeta(input, settings);
      callbacks.forEach(function (cb) { cb(val); });
      instance._fireChange();
      if (PB.checkStepValidity) PB.checkStepValidity();
    });

    var field = {
      el: wrap,
      value: function () { return instance._values[label] || ''; },
      setValue: function (v) {
        input.value = v || '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      },
      onChange: function (cb) { callbacks.push(cb); return field; },
      _input: input,
      _settings: settings,
      _label: label
    };
    instance._fields.push(field);
    return field;
  }

  function createLongTextField(instance, opts) {
    opts = opts || {};
    var label = opts.label || uid();
    var settings = {
      required: !!opts.required,
      min: parseInt(opts.min || 0, 10),
      max: parseInt(opts.max || 500, 10),
      placeholder: opts.placeholder || 'Type here...'
    };
    var settingsStr = 'required:' + settings.required + ', min:' + settings.min + ', max:' + settings.max;

    var wrap = document.createElement('div');
    wrap.className = 'pb-tk-field pb-tk-longtext-field';
    if (opts.fieldLabel) {
      wrap.innerHTML = '<span class="option-label">' + escapeHTML(opts.fieldLabel) + '</span>';
    }
    var ta = document.createElement('textarea');
    ta.className = 'builder-input pb-long-text pb-tk-input';
    ta.rows = opts.rows || 3;
    ta.placeholder = settings.placeholder;
    ta.dataset.label = label;
    ta.dataset.settings = settingsStr;
    wrap.appendChild(ta);

    var callbacks = [];
    ta.addEventListener('input', function () {
      var val = ta.value.trim();
      instance._values[label] = val;
      instance._syncToState();
      if (PB.validateTextInput) PB.validateTextInput(ta, settings);
      if (PB.updateInputMeta) PB.updateInputMeta(ta, settings);
      if (PB.resizeLongText) PB.resizeLongText(ta);
      callbacks.forEach(function (cb) { cb(val); });
      instance._fireChange();
      if (PB.checkStepValidity) PB.checkStepValidity();
    });

    var field = {
      el: wrap,
      value: function () { return instance._values[label] || ''; },
      setValue: function (v) {
        ta.value = v || '';
        ta.dispatchEvent(new Event('input', { bubbles: true }));
      },
      onChange: function (cb) { callbacks.push(cb); return field; },
      _input: ta,
      _settings: settings,
      _label: label
    };
    instance._fields.push(field);
    return field;
  }

  function createButtonChoice(instance, opts) {
    opts = opts || {};
    var label = opts.label || uid();
    var choices = opts.choices || [];
    var required = opts.required !== false;

    var wrap = document.createElement('div');
    wrap.className = 'pb-tk-field pb-tk-button-field';
    if (opts.fieldLabel) {
      wrap.innerHTML = '<span class="option-label">' + escapeHTML(opts.fieldLabel) + '</span>';
    }
    var grid = document.createElement('div');
    grid.className = 'btn-grid';
    grid.dataset.settings = required ? 'required:true' : '';

    choices.forEach(function (choice) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'builder-btn meta-btn';
      btn.dataset.label = label;
      btn.dataset.value = choice;
      btn.textContent = choice;
      btn.addEventListener('click', function () {
        grid.querySelectorAll('.builder-btn').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        instance._values[label] = choice;
        instance._syncToState();
        if (PB.validateButtonGrid) PB.validateButtonGrid(grid);
        callbacks.forEach(function (cb) { cb(choice); });
        instance._fireChange();
        if (PB.checkStepValidity) PB.checkStepValidity();
      });
      grid.appendChild(btn);
    });
    wrap.appendChild(grid);

    if (required) {
      grid.dataset.required = 'true';
      grid.dataset.valid = 'false';
    }

    var callbacks = [];
    var field = {
      el: wrap,
      value: function () { return instance._values[label] || ''; },
      setValue: function (v) {
        var val = String(v || '');
        var btns = grid.querySelectorAll('.builder-btn');
        for (var i = 0; i < btns.length; i++) {
          if (btns[i].dataset.value === val) { btns[i].click(); break; }
        }
      },
      onChange: function (cb) { callbacks.push(cb); return field; },
      _grid: grid,
      _label: label
    };
    instance._fields.push(field);
    return field;
  }

  function createUploadField(instance, opts) {
    opts = opts || {};
    var label = opts.label || uid();
    var required = !!opts.required;
    var accept = opts.accept || 'photo';
    var maxFiles = parseInt(opts.max_files || 1, 10);
    var acceptAttr = accept === 'video' ? 'video/*' : (accept === 'both' ? 'image/*,video/*' : 'image/*');
    var settingsStr = 'required:' + required + ', max_files:' + maxFiles + ', accept:' + accept;

    var wrap = document.createElement('div');
    wrap.className = 'pb-tk-field pb-tk-upload-field';
    if (opts.fieldLabel) {
      wrap.innerHTML = '<span class="option-label">' + escapeHTML(opts.fieldLabel) + '</span>';
    }

    var uploadWrap = document.createElement('div');
    uploadWrap.className = 'pb-upload-wrap pb-upload-inner';
    uploadWrap.dataset.label = label;
    uploadWrap.dataset.settings = settingsStr;
    uploadWrap.innerHTML =
      '<input type="hidden" class="builder-required" data-label="' + escapeHTML(label) + '" data-required="' + required + '" data-valid="false" value="[]">' +
      '<input type="file" class="pb-upload-file-input" style="display:none;" accept="' + acceptAttr + '" ' + (maxFiles > 1 ? 'multiple' : '') + ' onchange="window.productBuilder.handleFiles(this.closest(\'.pb-upload-wrap\'), this.files); this.value = \'\';">' +
      '<div class="pb-upload-row"><div class="pb-upload-preview-list"></div><button type="button" class="pb-upload-empty" onclick="window.productBuilder.triggerUploadInput(this)">+</button></div>' +
      '<div class="pb-upload-message" style="display:none;"></div>';
    wrap.appendChild(uploadWrap);

    var meta = {};
    var uploadCallbacks = [];

    var observer = new MutationObserver(function () {
      var hidden = uploadWrap.querySelector('input.builder-required');
      if (!hidden) return;
      var urls = [];
      try { urls = JSON.parse(hidden.value || '[]'); } catch (e) { /* noop */ }
      if (!Array.isArray(urls)) urls = [];
      instance._values[label] = urls.length === 1 ? urls[0] : (urls.length > 0 ? urls : '');
      instance._syncToState();
      uploadCallbacks.forEach(function (cb) { cb(urls); });
      instance._fireChange();
    });
    setTimeout(function () {
      var hidden = uploadWrap.querySelector('input.builder-required');
      if (hidden) observer.observe(hidden, { attributes: true, attributeFilter: ['value', 'data-valid'] });
    }, 50);

    var field = {
      el: wrap,
      value: function () { return instance._values[label] || ''; },
      meta: function (key) { return meta[key]; },
      setMeta: function (key, val) { meta[key] = val; },
      onUpload: function (cb) { uploadCallbacks.push(cb); return field; },
      _uploadWrap: uploadWrap,
      _label: label
    };
    instance._fields.push(field);
    return field;
  }

  function createAudioField(instance, opts) {
    opts = opts || {};
    var label = opts.label || uid();
    var required = !!opts.required;
    var reqAttr = required ? 'true' : 'false';

    var wrap = document.createElement('div');
    wrap.className = 'pb-tk-field pb-tk-audio-field';
    if (opts.fieldLabel) {
      wrap.innerHTML = '<span class="option-label">' + escapeHTML(opts.fieldLabel) + '</span>';
    }

    var audioWrap = document.createElement('div');
    audioWrap.className = 'pb-audio-wrap';
    audioWrap.dataset.label = label;
    audioWrap.innerHTML =
      '<input type="hidden" class="builder-required" data-label="' + escapeHTML(label) + '" data-required="' + reqAttr + '" data-valid="false" value="">' +
      '<div class="pb-audio-container">' +
        '<div class="pb-audio-state pb-state-initial" style="display:flex;">' +
          '<button type="button" class="pb-audio-btn-main" onclick="window.productBuilder.startAudioRecording(this)" aria-label="Record">' +
            '<svg class="pb-icon-mic" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"/></svg>' +
            'Record' +
          '</button>' +
        '</div>' +
        '<div class="pb-audio-state pb-state-recording" style="display:none;">' +
          '<span class="pb-audio-timer">00:00</span>' +
        '</div>' +
        '<div class="pb-audio-state pb-state-review" style="display:none;">' +
          '<div class="pb-audio-playback">' +
            '<div class="pb-audio-playback-bar">' +
              '<button type="button" class="pb-audio-btn-play" onclick="window.productBuilder.playAudioRecording(this)" aria-label="Play">' +
                '<svg class="pb-lt-icon-play" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>' +
                '<svg class="pb-lt-icon-pause" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" hidden><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>' +
              '</button>' +
              '<canvas class="pb-audio-wave-canvas pb-audio-wave-playback" height="32"></canvas>' +
            '</div>' +
            '<button type="button" class="pb-audio-btn-delete" onclick="window.productBuilder.clearAudioRecording(this)" aria-label="Delete recording">' +
              '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
        '<div class="pb-audio-message" style="display:none;"></div>' +
      '</div>';
    wrap.appendChild(audioWrap);

    var audioCallbacks = [];
    var observer = new MutationObserver(function () {
      var hidden = audioWrap.querySelector('input.builder-required');
      if (!hidden) return;
      var val = hidden.value || '';
      instance._values[label] = val;
      instance._syncToState();
      if (val) audioCallbacks.forEach(function (cb) { cb(val); });
      instance._fireChange();
    });
    setTimeout(function () {
      var hidden = audioWrap.querySelector('input.builder-required');
      if (hidden) observer.observe(hidden, { attributes: true, attributeFilter: ['value'] });
    }, 50);

    var field = {
      el: wrap,
      value: function () { return instance._values[label] || ''; },
      onRecord: function (cb) { audioCallbacks.push(cb); return field; },
      _audioWrap: audioWrap,
      _label: label
    };
    instance._fields.push(field);
    return field;
  }

  /* ── ToolkitInstance ────────────────────────────────── */

  function ToolkitInstance(inputsEl, previewEl, fieldConfig) {
    this._inputsEl = inputsEl;
    this._previewEl = previewEl;
    this._config = fieldConfig || {};
    this._fields = [];
    this._values = {};
    this._cartPropsFn = null;
    this._changeCallbacks = [];
    this._label = fieldConfig.label || '';
    this._apiEndpoint = (window.BUILDER_DATA && window.BUILDER_DATA.firebaseApiEndpoint) || '';
    this._layoutMode = 'split';
    this._customValidator = null;
  }

  ToolkitInstance.prototype.setCustomValidator = function (fn) {
    this._customValidator = fn;
    return this;
  };

  ToolkitInstance.prototype.textField = function (opts) {
    var f = createTextField(this, opts);
    this._inputsEl.appendChild(f.el);
    return f;
  };

  ToolkitInstance.prototype.longTextField = function (opts) {
    var f = createLongTextField(this, opts);
    this._inputsEl.appendChild(f.el);
    return f;
  };

  ToolkitInstance.prototype.buttonChoice = function (opts) {
    var f = createButtonChoice(this, opts);
    this._inputsEl.appendChild(f.el);
    return f;
  };

  ToolkitInstance.prototype.uploadField = function (opts) {
    var f = createUploadField(this, opts);
    this._inputsEl.appendChild(f.el);
    return f;
  };

  ToolkitInstance.prototype.audioField = function (opts) {
    var f = createAudioField(this, opts);
    this._inputsEl.appendChild(f.el);
    return f;
  };

  ToolkitInstance.prototype.previewHTML = function (html) {
    if (this._previewEl) {
      this._previewEl.innerHTML = html;
    }
    return this._previewEl;
  };

  ToolkitInstance.prototype.callAPI = function (action, payload) {
    var endpoint = this._apiEndpoint;
    if (!endpoint) {
      console.warn('[PBToolkit] No firebase_api_endpoint configured. Add shop metafield custom.firebase_api_endpoint in Shopify Admin > Settings > Custom data > Store.');
      return Promise.reject(new Error('No api_endpoint'));
    }
    var spinner = null;
    if (this._previewEl) {
      spinner = document.createElement('div');
      spinner.className = 'pb-tk-loading';
      spinner.innerHTML = '<div class="pb-tk-spinner"></div>';
      this._previewEl.appendChild(spinner);
    }
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: action, payload: payload })
    })
    .then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) {
          var msg = (json && json.error) ? json.error : 'Request failed';
          throw new Error(msg);
        }
        return json;
      });
    })
    .finally(function () {
      if (spinner && spinner.parentNode) spinner.parentNode.removeChild(spinner);
    });
  };

  ToolkitInstance.prototype.setCartProperties = function (fn) {
    this._cartPropsFn = fn;
  };

  ToolkitInstance.prototype.getCartProperties = function () {
    if (typeof this._cartPropsFn === 'function') return this._cartPropsFn();
    return this._values;
  };

  ToolkitInstance.prototype.validate = function () {
    var valid = true;
    if (typeof this._customValidator === 'function' && !this._customValidator()) valid = false;
    this._fields.forEach(function (f) {
      if (f._input) {
        var isValid = PB.validateTextInput ? PB.validateTextInput(f._input, f._settings) : true;
        if (!isValid) valid = false;
      }
      if (f._grid) {
        var gridValid = PB.validateButtonGrid ? PB.validateButtonGrid(f._grid) : true;
        if (!gridValid) valid = false;
      }
      if (f._uploadWrap) {
        var hidden = f._uploadWrap.querySelector('input.builder-required');
        if (hidden && hidden.dataset.required === 'true' && hidden.dataset.valid !== 'true') valid = false;
      }
      if (f._audioWrap) {
        var ahidden = f._audioWrap.querySelector('input.builder-required');
        if (ahidden && ahidden.dataset.required === 'true' && ahidden.dataset.valid !== 'true') valid = false;
      }
    });
    return valid;
  };

  ToolkitInstance.prototype.getValues = function () {
    var out = {};
    for (var k in this._values) {
      if (Object.prototype.hasOwnProperty.call(this._values, k)) out[k] = this._values[k];
    }
    return out;
  };

  ToolkitInstance.prototype.onAnyChange = function (cb) {
    this._changeCallbacks.push(cb);
    return this;
  };

  ToolkitInstance.prototype.setLayout = function (mode) {
    this._layoutMode = mode === 'full' ? 'full' : 'split';
    var container = document.getElementById('main-flow-container') || document.getElementById('sub-flow-container');
    if (!container) return;
    if (this._layoutMode === 'full') {
      container.classList.add('pb-layout-full');
    } else {
      container.classList.remove('pb-layout-full');
    }
  };

  ToolkitInstance.prototype._fireChange = function () {
    var vals = this.getValues();
    this._changeCallbacks.forEach(function (cb) { cb(vals); });
  };

  ToolkitInstance.prototype._syncToState = function () {
    var ctx = PB.getActiveContext ? PB.getActiveContext() : null;
    if (!ctx) return;
    var props = this.getCartProperties();
    ctx.selections[this._label] = props;
  };

  ToolkitInstance.prototype._setupScrollToNext = function () {
    var fields = this._fields;
    if (fields.length < 2) return;
    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var behavior = reducedMotion ? 'auto' : 'smooth';

    function scrollToNext(currentField) {
      var idx = fields.indexOf(currentField);
      if (idx >= 0 && idx < fields.length - 1) {
        var next = fields[idx + 1];
        var el = next.el;
        el.scrollIntoView({ behavior: behavior, block: 'nearest' });
        var focusable = el.querySelector('input, .builder-input, .builder-btn');
        if (focusable) {
          try { focusable.focus(); } catch (e) {}
        }
      }
    }

    fields.forEach(function (f, i) {
      if (i >= fields.length - 1) return;
      if (f._input) {
        var wrap = f.el;
        if (wrap && wrap.classList.contains('pb-tk-longtext-field')) return;
        f._input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            scrollToNext(f);
          }
        });
      }
      if (f._grid) {
        f.onChange(function () { scrollToNext(f); });
      }
    });
  };

  ToolkitInstance.prototype.hydrateFromSelections = function () {
    var ctx = PB.getActiveContext ? PB.getActiveContext() : null;
    if (!ctx || !this._label) return;
    var saved = ctx.selections[this._label];
    if (saved == null || saved === '') return;
    if (typeof saved !== 'object' || Array.isArray(saved)) return;

    var self = this;
    Object.keys(saved).forEach(function (key) {
      self._values[key] = saved[key];
      var field = self._fields.find(function (f) { return f._label === key; });
      if (field && typeof field.setValue === 'function') {
        field.setValue(saved[key]);
      }
    });
    this._syncToState();
    this._fireChange();
  };

  ToolkitInstance.prototype.destroy = function () {
    this._fields = [];
    this._values = {};
    this._changeCallbacks = [];
    this._cartPropsFn = null;
    if (this._inputsEl) this._inputsEl.innerHTML = '';
  };

  /* ── Public API ──────────────────────────────────────── */

  function initCustomField(inputsEl, previewEl, fieldConfig) {
    if (!inputsEl) return null;
    var code = inputsEl.getAttribute('data-custom-code') || inputsEl.dataset.customCode || '';
    if (!code || !code.trim()) {
      console.warn('[PBToolkit] Custom field has no code in data-custom-code.');
      inputsEl.innerHTML = '<div class="pb-tk-error">No custom code provided.</div>';
      return null;
    }

    var instanceKey = (fieldConfig.label || '') + '__' + Date.now();
    if (instances[instanceKey]) {
      instances[instanceKey].destroy();
    }

    var inst = new ToolkitInstance(inputsEl, previewEl, fieldConfig);
    instances[instanceKey] = inst;
    inputsEl._pbtkInstance = inst;

    try {
      var fn = new Function('toolkit', 'previewEl', code);
      fn(inst, previewEl);
      inst._setupScrollToNext();
      inst.hydrateFromSelections();
    } catch (err) {
      console.error('[PBToolkit] Custom field error:', err);
      inputsEl.innerHTML = '<div class="pb-tk-error">Custom field error: ' + escapeHTML(err.message) + '</div>';
      return null;
    }

    return inst;
  }

  function getInstanceForStep(stepEl) {
    if (!stepEl) return null;
    var inputsEl = stepEl.querySelector('.pb-custom-inputs');
    if (!inputsEl) return null;
    return inputsEl._pbtkInstance || null;
  }

  function validateCustomStep(stepEl) {
    var inst = getInstanceForStep(stepEl);
    return inst ? inst.validate() : true;
  }

  window.PBToolkit = {
    init: initCustomField,
    getInstance: getInstanceForStep,
    validateStep: validateCustomStep,
    _instances: instances
  };

})();
